/**
 * Back returns to exactly where you were (docs/ux/PEEK-AND-RESUME.md §4.5): each history entry keeps its scroll in
 * sessionStorage, and a Back or Forward (POP) puts it back once the page has grown tall enough (screens load their parts
 * after they appear). Before, Back from "See it in today's records" landed on Checks at the top (MEASURED: 2,421 → 0).
 * A link with a #section is left to the section scroll (Layout useHashScroll); a person who scrolls first wins.
 */
import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const key = (k: string) => `cc.scroll.${k}`;

export function useScrollRestore() {
  const loc = useLocation();
  const type = useNavigationType();
  useEffect(() => { try { if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual"; } catch { /* ignore */ } }, []);
  // Keep this entry's scroll as the person moves.
  useEffect(() => {
    const save = () => { try { sessionStorage.setItem(key(loc.key), String(Math.round(window.scrollY))); } catch { /* ignore */ } };
    window.addEventListener("scroll", save, { passive: true });
    return () => window.removeEventListener("scroll", save);
  }, [loc.key]);
  // Back or Forward: put it back.
  useEffect(() => {
    if (type !== "POP" || loc.hash) return;
    let target: number;
    try { target = Number(sessionStorage.getItem(key(loc.key)) ?? "NaN"); } catch { return; }
    if (!Number.isFinite(target) || target <= 0) return;
    let touched = false, tries = 0;
    const stop = () => { touched = true; };
    window.addEventListener("wheel", stop, { passive: true }); window.addEventListener("touchstart", stop, { passive: true }); window.addEventListener("keydown", stop);
    const t = setInterval(() => {
      if (touched || ++tries > 30) { clearInterval(t); return; }
      window.scrollTo(0, target);
      if (Math.abs(window.scrollY - target) < 2 && tries > 2) clearInterval(t);
    }, 70);
    return () => { clearInterval(t); window.removeEventListener("wheel", stop); window.removeEventListener("touchstart", stop); window.removeEventListener("keydown", stop); };
  }, [loc.key, loc.hash, type]);
}
